import { FileText, Loader2, Sparkles, X } from 'lucide-react';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { KnowledgeSourceModal } from '../conversation';
import {
  Badge,
  Button,
  BuyTokensModal,
  Input,
  InsufficientTokensModal,
} from '../ui';
import { useAuth } from '../../contexts';
import { useKnowledgeSources, useTokens } from '../../hooks';
import { ExecutingAgentService } from '../../services';
import { KnowledgeSource } from '../../types/entities';
import { parseStreamResult, readStreamFinalMessage } from '../../utils/aiStream';

export interface AgentFormValues {
  name: string;
  role: string;
  goal: string;
  backstory: string;
  tools: string[];
  tool_mode: 'creative' | 'accuracy';
  verbose: boolean;
  allow_delegation: boolean;
  max_iter: string;
  max_execution_time: string;
  is_active: boolean;
}

export interface AgentFormPanelHandle {
  submit: () => Promise<boolean>;
}

interface AgentFormPanelProps {
  mode: 'create' | 'edit';
  creationMode?: 'ai' | 'manual';
  organizationId?: number;
  defaultValues: AgentFormValues;
  submitLabel: string;
  onSubmit: (values: AgentFormValues) => Promise<void | boolean> | void | boolean;
  onCancel?: () => void;
  layout?: 'split' | 'stack';
  showSubmitButton?: boolean;
  showForm?: boolean;
  onGenerateSuccess?: (values: AgentFormValues) => void;
}

interface GeneratedAgentData {
  name?: string;
  role?: string;
  goal?: string;
  backstory?: string;
  tools?: string[];
  tool_mode?: 'creative' | 'accuracy';
  verbose?: boolean;
  allow_delegation?: boolean;
  max_iter?: string | number;
  max_execution_time?: string | number;
  is_active?: boolean;
}

const AgentFormPanel = forwardRef<AgentFormPanelHandle, AgentFormPanelProps>(
  (
    {
      mode,
      creationMode = 'ai',
      organizationId,
      defaultValues,
      submitLabel,
      onSubmit,
      onCancel,
      layout = 'split',
      showSubmitButton = true,
      showForm = true,
      onGenerateSuccess,
    },
    ref
  ) => {
  const { t } = useTranslation();
  const { refreshUser } = useAuth();
  const {
    checkTokensBeforeAction,
    tokensBalance,
    isModalOpen,
    closeModal,
    isBuyTokensModalOpen,
    closeBuyTokensModal,
  } = useTokens();
  const { knowledgeSources, loadKnowledgeSources, toggleKnowledgeSource, isLoadingKnowledge } =
    useKnowledgeSources(organizationId);
  const executingAgentService = useMemo(() => new ExecutingAgentService(), []);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    watch,
    getValues,
  } = useForm<AgentFormValues>({ defaultValues });

  const [isGenerating, setIsGenerating] = useState(false);
  const [description, setDescription] = useState('');
  const [selectedKnowledgeSources, setSelectedKnowledgeSources] = useState<
    KnowledgeSource[]
  >([]);
  const [showKnowledgeSourceModal, setShowKnowledgeSourceModal] =
    useState(false);

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  useEffect(() => {
    if (!organizationId) {
      return;
    }
    loadKnowledgeSources();
  }, [loadKnowledgeSources, organizationId]);

  /**
   * Aplica os dados gerados pela IA no formulário.
   * @param agentData Dados retornados pela IA
   * @param requireCoreFields Define se valida campos obrigatórios
   */
  const applyGeneratedAgentData = (
    agentData: GeneratedAgentData,
    requireCoreFields: boolean
  ): AgentFormValues => {
    if (
      requireCoreFields &&
      (!agentData?.name || !agentData?.role || !agentData?.goal)
    ) {
      throw new Error(
        'Resposta incompleta: faltam campos obrigatórios (name, role, goal)'
      );
    }

    const currentValues = getValues();
    const inferredMode = Array.isArray(agentData?.tools)
      ? mapToolsToMode(agentData.tools)
      : currentValues.tool_mode;
    const updatedValues: AgentFormValues = {
      name: agentData?.name || currentValues.name,
      role: agentData?.role || currentValues.role,
      goal: agentData?.goal || currentValues.goal,
      backstory:
        agentData?.backstory !== undefined
          ? agentData.backstory
          : currentValues.backstory || '',
      tools: Array.isArray(agentData?.tools)
        ? agentData.tools
        : currentValues.tools,
      tool_mode: agentData?.tool_mode || inferredMode,
      verbose:
        agentData?.verbose !== undefined
          ? agentData.verbose
          : currentValues.verbose,
      allow_delegation:
        agentData?.allow_delegation !== undefined
          ? agentData.allow_delegation
          : currentValues.allow_delegation,
      max_iter:
        agentData?.max_iter !== undefined
          ? String(agentData.max_iter)
          : currentValues.max_iter,
      max_execution_time:
        agentData?.max_execution_time !== undefined
          ? String(agentData.max_execution_time)
          : currentValues.max_execution_time,
      is_active:
        agentData?.is_active !== undefined
          ? agentData.is_active
          : currentValues.is_active,
    };

    Object.entries(updatedValues).forEach(([key, value]) => {
      setValue(key as keyof AgentFormValues, value, { shouldDirty: true });
    });

    return updatedValues;
  };

  /**
   * Extrai os dados do agente do payload retornado pela IA.
   * @param resultData Resultado parseado do stream
   * @returns Dados do agente
   */
  const getAgentFromResult = (resultData: any): GeneratedAgentData => {
    if (resultData?.agentes && Array.isArray(resultData.agentes)) {
      return resultData.agentes[0] || {};
    }
    if (resultData?.agente) {
      return resultData.agente;
    }
    if (resultData?.agent) {
      return resultData.agent;
    }
    return resultData || {};
  };

  const mapToolsToMode = (tools: string[] | undefined): 'creative' | 'accuracy' => {
    if (!Array.isArray(tools) || tools.length === 0) {
      return 'accuracy';
    }
    const normalizedTools = tools.map(tool => (tool || '').toLowerCase().trim());
    const hasRagTool = normalizedTools.includes('ragtool');
    const hasCodeInterpreterTool = normalizedTools.includes('codeinterpretertool');

    if (hasRagTool && !hasCodeInterpreterTool) {
      return 'creative';
    }
    if (hasCodeInterpreterTool && !hasRagTool) {
      return 'accuracy';
    }
    return 'accuracy';
  };

  const handleGenerate = async () => {
    if (!description.trim()) {
      toast.error(
        mode === 'create'
          ? 'Por favor, descreva o agente que deseja criar'
          : 'Por favor, descreva as alterações desejadas para o agente'
      );
      return;
    }

    if (!checkTokensBeforeAction()) {
      return;
    }

    setIsGenerating(true);

    try {
      const knowledgeSourceIds = selectedKnowledgeSources.map(ks => ks.id);

      if (mode === 'edit') {
        const currentValues = getValues();
        const currentAgentContext = `
Agente atual (dados do formulário):
- Nome: ${currentValues.name || 'Não definido'}
- Role: ${currentValues.role || 'Não definido'}
- Goal: ${currentValues.goal || 'Não definido'}
- Backstory: ${currentValues.backstory || 'Não definido'}
- Verbose: ${currentValues.verbose}
- Allow Delegation: ${currentValues.allow_delegation}
- Max Iter: ${currentValues.max_iter || '5'}
- Max Execution Time: ${currentValues.max_execution_time || '300'}
- Is Active: ${currentValues.is_active}
`;

        const descriptionText = `${currentAgentContext}

Alterações desejadas: ${description}

Com base nas informações do agente atual e nas alterações solicitadas, retorne APENAS um JSON válido com os seguintes campos atualizados: name (string), role (string), goal (string), backstory (string), tools (array de strings), verbose (boolean), allow_delegation (boolean), max_iter (string), max_execution_time (string).`;

        const stream = await executingAgentService.generateAgentStream({
          description: descriptionText,
          rag_knowledge_source_ids:
            knowledgeSourceIds.length > 0 ? knowledgeSourceIds : undefined,
          user_message: description,
        });

        const rawResponse = await readStreamFinalMessage(stream);
        const resultData = parseStreamResult(rawResponse);
        const agentData = getAgentFromResult(resultData);
        const updatedValues = applyGeneratedAgentData(agentData, false);
        onGenerateSuccess?.(updatedValues);

        toast.success(
          'Agente atualizado com sucesso! Revise os campos e ajuste se necessário.'
        );
      } else {
        const stream = await executingAgentService.generateAgentStream({
          description,
          rag_knowledge_source_ids:
            knowledgeSourceIds.length > 0 ? knowledgeSourceIds : undefined,
          user_message: description,
        });

        const rawResponse = await readStreamFinalMessage(stream);
        const resultData = parseStreamResult(rawResponse);
        const agentData = getAgentFromResult(resultData);
        const updatedValues = applyGeneratedAgentData(agentData, true);
        onGenerateSuccess?.(updatedValues);

        toast.success(
          'Agente gerado com sucesso! Revise os campos e ajuste se necessário.'
        );
      }

      await refreshUser();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Erro desconhecido';
      toast.error(`Erro ao gerar agente: ${errorMessage}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const isSplitLayout = layout === 'split';
  const isManualMode = creationMode === 'manual';
  const shouldShowGenerator = !isManualMode;
  const shouldShowForm = isManualMode ? true : showForm;
  const selectedToolMode = watch('tool_mode');
  const normalizedMaxIterations = defaultValues.max_iter || '5';
  const normalizedMaxExecutionTime = defaultValues.max_execution_time || '300';

  const normalizeAgentValues = (values: AgentFormValues): AgentFormValues => ({
    ...values,
    verbose: true,
    allow_delegation: true,
    max_iter: normalizedMaxIterations,
    max_execution_time: normalizedMaxExecutionTime,
  });

  useImperativeHandle(ref, () => ({
    submit: () =>
      new Promise(resolve => {
        handleSubmit(
          async values => {
            try {
              const result = await onSubmit(normalizeAgentValues(values));
              resolve(result !== false);
            } catch {
              resolve(false);
            }
          },
          () => resolve(false)
        )();
      }),
  }));

  return (
    <div className={`flex h-full ${isSplitLayout ? 'flex-row' : 'flex-col'}`}>
      {shouldShowGenerator && (
        <div
          className={`${
            isSplitLayout
              ? 'flex-[1.5] flex flex-col bg-light-50 dark:bg-graphite-900 overflow-hidden'
              : 'p-6 border-b border-gray-200 dark:border-gray-700'
          }`}
        >
          <div
            className={`${
              isSplitLayout ? 'p-6 border-b border-gray-200 dark:border-gray-700' : 'mb-4'
            }`}
          >
            <div className="flex items-center space-x-2 mb-2">
              <Sparkles className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {mode === 'create'
                  ? 'Descreva o agente que deseja criar'
                  : 'Descreva as alterações desejadas'}
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {mode === 'create'
                ? 'Descreva em detalhes como você quer que o agente seja. O assistente irá gerar automaticamente os campos do formulário.'
                : 'Descreva as alterações que deseja fazer no agente. O assistente irá atualizar os campos do formulário considerando as informações atuais.'}
            </p>
          </div>

          <div className={isSplitLayout ? 'flex-1 p-6 flex flex-col' : ''}>
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Fontes de Conhecimento (opcional)
                </label>
                <Button
                  variant="outline"
                  onClick={() => setShowKnowledgeSourceModal(true)}
                >
                  <FileText className="h-4 w-4 mr-1" />
                  Selecionar
                </Button>
              </div>
              {selectedKnowledgeSources.length > 0 ? (
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedKnowledgeSources.map(source => (
                    <Badge
                      key={source.id}
                      variant="primary"
                      className="flex items-center space-x-1"
                    >
                      <span>{source.name}</span>
                      <button
                        onClick={() =>
                          setSelectedKnowledgeSources(prev =>
                            prev.filter(s => s.id !== source.id)
                          )
                        }
                        className="ml-1 hover:text-red-500 dark:hover:text-red-400"
                        type="button"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Nenhuma fonte selecionada
                </p>
              )}
            </div>

            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              className="w-full px-4 py-3 border border-light-300 dark:border-graphite-600 bg-white dark:bg-graphite-800 text-deepTechBlue-500 dark:text-light-50 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
              style={{ height: isSplitLayout ? '250px' : '200px' }}
              placeholder={
                mode === 'create'
                  ? 'Exemplo: Quero um agente especialista em análise de dados que seja capaz de processar grandes volumes de informações...'
                  : 'Exemplo: Quero que o agente seja mais detalhado nas respostas, aumente o max_iter para 100...'
              }
            />
            <div className="mt-4 flex justify-end">
              <Button
                variant="outline"
                onClick={handleGenerate}
                disabled={isGenerating || !description.trim()}
                className="min-w-[150px]"
                type="button"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Gerando...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    {mode === 'create' ? 'Gerar Agente' : 'Atualizar Agente'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {shouldShowForm && (
        <form
          onSubmit={handleSubmit(values => onSubmit(normalizeAgentValues(values)))}
          className={`${
            isSplitLayout
              ? shouldShowGenerator
                ? 'flex-[2] overflow-y-auto p-6 border-l border-gray-200 dark:border-gray-700'
                : 'flex-1 overflow-y-auto p-6'
              : 'p-6'
          }`}
        >
          <div className="space-y-4 max-w-2xl mx-auto">
            <Input
              label={t('agents.agentName')}
              error={errors.name?.message}
              placeholder={t('agents.agentNamePlaceholder')}
              {...register('name', { required: 'Nome é obrigatório' })}
            />

            <Input
              label={t('agents.role')}
              error={errors.role?.message}
              placeholder={t('agents.rolePlaceholder')}
              {...register('role')}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('agents.goal')}
              </label>
              <textarea
                className="w-full px-3 py-2 border border-light-300 dark:border-graphite-600 bg-white dark:bg-graphite-800 text-deepTechBlue-500 dark:text-light-50 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                rows={10}
                placeholder={t('agents.goalPlaceholder')}
                {...register('goal', { required: 'Objetivo é obrigatório' })}
              />
              {errors.goal?.message && (
                <p className="mt-1 text-sm text-error-600 dark:text-error-400">
                  {errors.goal?.message}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('agents.backstory')}
              </label>
              <textarea
                className="w-full px-3 py-2 border border-light-300 dark:border-graphite-600 bg-white dark:bg-graphite-800 text-deepTechBlue-500 dark:text-light-50 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                rows={10}
                placeholder={t('agents.backstoryPlaceholder')}
                {...register('backstory')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('agents.toolMode')}
              </label>
              <input type="hidden" {...register('tool_mode')} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className={
                    selectedToolMode === 'creative'
                      ? 'border-primary-400 bg-primary-50 text-primary-700 dark:border-primary-500 dark:bg-primary-900/30 dark:text-primary-300'
                      : ''
                  }
                  onClick={() =>
                    setValue('tool_mode', 'creative', { shouldDirty: true })
                  }
                >
                  {t('agents.modeCreative')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className={
                    selectedToolMode === 'accuracy'
                      ? 'border-primary-400 bg-primary-50 text-primary-700 dark:border-primary-500 dark:bg-primary-900/30 dark:text-primary-300'
                      : ''
                  }
                  onClick={() =>
                    setValue('tool_mode', 'accuracy', { shouldDirty: true })
                  }
                >
                  {t('agents.modeAccuracy')}
                </Button>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  {...register('is_active')}
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  {t('agents.active')}
                </span>
              </label>
            </div>

            {(onCancel || showSubmitButton) && (
              <div className="flex justify-end space-x-2">
                {onCancel && (
                  <Button type="button" variant="outline" onClick={onCancel}>
                    {t('common.cancel')}
                  </Button>
                )}
                {showSubmitButton && (
                  <Button type="submit" variant="primary" disabled={isSubmitting}>
                    {submitLabel}
                  </Button>
                )}
              </div>
            )}
          </div>
        </form>
      )}

      <KnowledgeSourceModal
        isOpen={showKnowledgeSourceModal}
        onClose={() => setShowKnowledgeSourceModal(false)}
        knowledgeSources={knowledgeSources}
        selectedKnowledgeSources={selectedKnowledgeSources}
        isLoading={isLoadingKnowledge}
        onToggleSource={source => {
          toggleKnowledgeSource(
            source,
            selectedKnowledgeSources,
            setSelectedKnowledgeSources
          );
        }}
      />

      <InsufficientTokensModal
        isOpen={isModalOpen}
        onClose={closeModal}
        tokensBalance={tokensBalance}
      />
      <BuyTokensModal
        isOpen={isBuyTokensModalOpen}
        onClose={closeBuyTokensModal}
      />
    </div>
  );
  }
);

export default AgentFormPanel;
